import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaArrowLeft, FaCalendarAlt, FaMoneyBillWave, FaClock, FaBriefcase, FaEdit } from 'react-icons/fa';

function HireWorkerPage() {
    const { workerId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Form State
    const [hireMode, setHireMode] = useState('new'); // 'new' or 'existing'
    const [selectedJobId, setSelectedJobId] = useState('');
    const [myJobs, setMyJobs] = useState([]);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [rate, setRate] = useState('');
    const [unit, setUnit] = useState('day');
    const [description, setDescription] = useState('');

    // UI State
    const [loading, setLoading] = useState(false);
    const [worker, setWorker] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch basic worker info
                const workerRes = await apiClient.get(`/workers/${workerId}`);
                setWorker(workerRes.data);

                // Fetch Buyer's Jobs
                const jobsRes = await apiClient.get('/jobs/my-jobs?type=running'); // or type=all
                setMyJobs(jobsRes.data);
            } catch (err) {
                setError('Failed to load data.');
                console.error(err);
            }
        };
        fetchData();
    }, [workerId]);

    const handleJobSelect = (e) => {
        const jobId = e.target.value;
        setSelectedJobId(jobId);
        if (jobId) {
            const job = myJobs.find(j => j.id === jobId);
            if (job) {
                setDescription(job.description || ''); // Auto-fill description
            }
        }
    };

    const calculateTotal = () => {
        if (!rate || !startDate || !endDate) return 0;
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (end < start) return 0;

        const timeDiff = Math.abs(end.getTime() - start.getTime());
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;

        if (unit === 'day') return rate * daysDiff;
        if (unit === 'contract') return parseFloat(rate);
        if (unit === 'hour') return rate * daysDiff * 8; // Estimate
        return 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (new Date(endDate) < new Date(startDate)) {
            setError('End date must be after start date.');
            return;
        }

        if (hireMode === 'existing' && !selectedJobId) {
            setError('Please select an existing job.');
            return;
        }

        setLoading(true);
        try {
            await apiClient.post('/hires', {
                worker_profile_id: worker.profile.id,
                start_date: startDate,
                end_date: endDate,
                rate: parseFloat(rate),
                unit,
                description,
                job_post_id: hireMode === 'existing' ? selectedJobId : null
            });
            setSuccess('Hire confirmed successfully!');
            setTimeout(() => navigate('/owner-dashboard'), 2000);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Failed to confirm hire.');
        } finally {
            setLoading(false);
        }
    };

    if (!worker) return <div className="p-10 text-center">Loading worker details...</div>;

    const { profile } = worker;
    return (
        <div className="max-w-3xl mx-auto p-6">
            <button onClick={() => navigate(-1)} className="flex items-center text-blue-600 hover:underline mb-6">
                <FaArrowLeft className="mr-2" /> Back
            </button>
            <div className="bg-white rounded-xl shadow-md overflow-hidden p-8">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Hire {profile?.name || 'Worker'}</h2>

                {/* Worker Summary */}
                <div className="flex items-center mb-8 bg-blue-50 p-4 rounded-lg">
                    {profile?.picture ? (
                        <img src={loadImage(profile.picture)} alt={profile.name} className="w-16 h-16 rounded-full object-cover mr-4" />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 mr-4">
                            <FaUser size={24} />
                        </div>
                    )}
                    <div>
                        <p className="font-semibold text-lg">{profile?.name}</p>
                        <p className="text-gray-600 text-sm">{profile?.location?.district || 'Location not specified'}</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex mb-6 border-b">
                    <button
                        className={`pb-2 px-4 font-semibold ${hireMode === 'new' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                        onClick={() => setHireMode('new')}
                    >
                        <FaEdit className="inline mr-2" />
                        New Contract
                    </button>
                    <button
                        className={`pb-2 px-4 font-semibold ${hireMode === 'existing' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                        onClick={() => setHireMode('existing')}
                    >
                        <FaBriefcase className="inline mr-2" />
                        Select Existing Job
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">

                    {hireMode === 'existing' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Select Job</label>
                            <select
                                value={selectedJobId}
                                onChange={handleJobSelect}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                            >
                                <option value="">-- Choose a posted job --</option>
                                {myJobs.map(job => (
                                    <option key={job.id} value={job.id}>{job.title} ({job.job_type})</option>
                                ))}
                            </select>
                            {myJobs.length === 0 && <p className="text-sm text-red-500 mt-1">No active jobs found.</p>}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <div className="relative">
                                <FaCalendarAlt className="absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border p-2"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <div className="relative">
                                <FaCalendarAlt className="absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border p-2"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Unit</label>
                            <div className="relative">
                                <FaClock className="absolute left-3 top-3 text-gray-400" />
                                <select
                                    value={unit}
                                    onChange={(e) => setUnit(e.target.value)}
                                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border p-2"
                                >
                                    <option value="day">Per Day</option>
                                    <option value="hour">Per Hour</option>
                                    <option value="contract">Fixed Contract</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rate / Amount (BDT)</label>
                            <div className="relative">
                                <FaMoneyBillWave className="absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="number"
                                    value={rate}
                                    onChange={(e) => setRate(e.target.value)}
                                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border p-2"
                                    placeholder="0.00"
                                    step="0.01"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description / Work Details</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows="4"
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 border p-2"
                            placeholder="Describe the work to be done..."
                        ></textarea>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-md">
                        <p className="text-right text-lg font-bold text-gray-800">
                            Estimated Total: {calculateTotal().toFixed(2)} BDT
                        </p>
                    </div>

                    {error && <div className="text-red-600 bg-red-50 p-3 rounded-md">{error}</div>}
                    {success && <div className="text-green-600 bg-green-50 p-3 rounded-md">{success}</div>}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                        {loading ? 'Processing...' : 'Confirm Hire'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default HireWorkerPage;
